import { Button, Grid, Typography } from '@mui/material'
import { CheckCircle } from '@mui/icons-material'
import { Field, FormikHelpers, getIn } from 'formik'
import { inject } from 'mobx-react'
import moment from 'moment'
import * as React from 'react'
import ESDialog from '../common/ESDialog'
import FullWidthField from '../common/FullWidthField'
import InterceptCheckboxWithLabel from '../common/InterceptCheckboxWithLabel'
import { IScheduleAppointmentTask, IUser } from '../Definitions'
import { IStores } from '../Stores'
import {
  createIScheduleAppointmentTaskFormValues,
  IScheduleAppointmentTaskFormValues,
  ScheduleAppointmentTaskSchema,
  toScheduleAppointmentTask,
} from './ScheduleAppointmentTaskFormValues'

interface IScheduleAppointmentTaskDialogProps {
  close?: () => void
  isLoading?: boolean
  isOpen?: boolean
  saveScheduleAppointmentTask?: (
    scheduleAppointmentTask: IScheduleAppointmentTask
  ) => Promise<void>
  scheduleAppointmentTask?: IScheduleAppointmentTask
  user?: IUser
}

class ScheduleAppointmentTaskDialog extends React.Component<IScheduleAppointmentTaskDialogProps> {
  public save = async (
    values: IScheduleAppointmentTaskFormValues,
    formikBag: FormikHelpers<IScheduleAppointmentTaskFormValues>
  ) => {
    if (values.dateCompleted) {
      values.dateCompleted = moment(values.dateCompleted).toDate()
    }
    const scheduleAppointmentTask = toScheduleAppointmentTask(
      values,
      this.props.scheduleAppointmentTask
    )

    this.props.saveScheduleAppointmentTask!(scheduleAppointmentTask).finally(() =>
      formikBag.setSubmitting(false)
    )
  }

  public render() {
    const { close, isLoading, isOpen, scheduleAppointmentTask } = this.props
    const hasValue = (field: string, values: IScheduleAppointmentTaskFormValues) => {
      const value = getIn(values, field)

      return !!value
    }

    const lastUpdated = scheduleAppointmentTask!.updatedByUser
      ? `Last updated by ${scheduleAppointmentTask!.updatedByUser!.name} on ${moment(
          scheduleAppointmentTask!.modifiedOn
        ).format('MM/DD/YYYY')} at ${moment(scheduleAppointmentTask!.modifiedOn).format(
          'hh:mm A'
        )}`
      : ''

    const timingNotice = scheduleAppointmentTask
      ? scheduleAppointmentTask.days !== 0
        ? `This task should be completed by ${
            scheduleAppointmentTask.schedule!.appointmentDate
              ? moment(scheduleAppointmentTask.dueDate).format('MM/DD/YYYY')
              : 'TBD'
          }.`
        : 'This task does not have a due date.'
      : ''
    return (
      <>
        {scheduleAppointmentTask ? (
          <ESDialog
            close={close!}
            initialValues={createIScheduleAppointmentTaskFormValues(
              scheduleAppointmentTask
            )}
            isLoading={isLoading}
            open={isOpen!}
            save={this.save}
            title={'Appointment Task'}
            titleIcon={<CheckCircle />}
            validationSchema={ScheduleAppointmentTaskSchema}
          >
            {({ values, setFieldValue }) => (
              <Grid container direction="column" spacing={3}>
                <Grid item xs={6}>
                  <Typography
                    variant="h6"
                    style={{
                      paddingTop: '12px',
                      paddingBottom: '12px',
                    }}
                  >
                    {scheduleAppointmentTask.name}
                  </Typography>
                </Grid>
                <Grid
                  style={{
                    marginLeft: '12px',
                    marginBottom: '10px',
                    paddingBottom: '12px',
                  }}
                >
                  {scheduleAppointmentTask.timing !== '' ? (
                    <i style={{ padding: '0px 0px' }}>{timingNotice}</i>
                  ) : (
                    <></>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Grid container direction="row" spacing={3}>
                    <Grid item xs={3}>
                      <Grid container direction="column" spacing={2}>
                        <Grid item>
                          <FullWidthField
                            format="YYYY-MM-DD"
                            defaultValue={values.dateCompletedInput}
                            type="date"
                            name="dateCompletedInput"
                            label="Completed Date"
                            inputId="dateCompleted"
                            fullWidth={true}
                            clearable={true}
                            shrink={hasValue('dateCompletedInput', values)}
                            variant="outlined"
                            disabled={values.taskNotApplicable}
                            onChange={this.handleDateChange(setFieldValue)}
                            InputProps={{
                              sx: {
                                input: {
                                  colorScheme: (theme: any) => theme.palette.mode,
                                },
                              },
                            }}
                          />
                        </Grid>
                        <Grid item>
                          <FullWidthField
                            defaultValue={values.completedTime}
                            format="hh:mm:ss"
                            type="time"
                            name="completedTime"
                            label="Completed Time"
                            inputId="completedTime"
                            fullWidth={true}
                            clearable={true}
                            shrink={hasValue('completedTime', values)}
                            variant="outlined"
                            disabled={values.taskNotApplicable}
                            onChange={this.handleTimeChange(setFieldValue)}
                            InputProps={{
                              sx: {
                                input: {
                                  colorScheme: (theme: any) => theme.palette.mode,
                                },
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={3}>
                      <Button
                        color="primary"
                        variant="contained"
                        style={{ marginTop: '8px' }}
                        onClick={this.handleMarkAsCompleteClicked(setFieldValue)}
                        disabled={values.taskNotApplicable}
                      >
                        Mark as Complete
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        name="taskNotApplicable"
                        Label={{
                          label: 'This task is not applicable for this appointment',
                        }}
                        component={InterceptCheckboxWithLabel}
                        onChange={this.onTaskNotApplicableChange(setFieldValue)}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <FullWidthField
                    shrink={hasValue('comments', values)}
                    name="comments"
                    label="Comments"
                    rows="6"
                    multiline={true}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  style={{
                    marginLeft: '16px',
                    marginTop: '10px',
                    fontStyle: 'italics',
                  }}
                >
                  <span>{lastUpdated}</span>
                </Grid>
              </Grid>
            )}
          </ESDialog>
        ) : (
          <></>
        )}
      </>
    )
  }

  public onTaskNotApplicableChange =
    (setFieldValue: (name: string, value: any) => void) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setFieldValue('dateCompleted', undefined)
        setFieldValue('dateCompletedInput', '')
        setFieldValue('completedTime', '')
      }
    }

  public handleMarkAsCompleteClicked =
    (setFieldValue: (name: string, value: any) => void) => () => {
      const appointmentDate =
        this.props.scheduleAppointmentTask?.isWorkRestrictionTask === true
          ? this.props.scheduleAppointmentTask!.schedule!.appointmentDate
          : new Date()

      const completedDate = appointmentDate ? new Date(appointmentDate) : new Date()
      const formattedDate = moment(completedDate).format('YYYY-MM-DD')
      const formattedTime = moment(completedDate).format('HH:mm')

      setFieldValue('dateCompleted', completedDate)
      setFieldValue('dateCompletedInput', formattedDate)
      setFieldValue('completedTime', formattedTime)
    }

  public handleDateChange =
    (setFieldValue: (name: string, value: any) => void) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value
      setFieldValue('dateCompletedInput', newValue)
      if (newValue) {
        setFieldValue('dateCompleted', moment(newValue).toDate())
      } else {
        setFieldValue('dateCompleted', undefined)
      }
    }

  public handleTimeChange =
    (setFieldValue: (name: string, value: any) => void) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value
      setFieldValue('completedTime', newValue)
    }
}

const InjectedScheduleAppointmentTaskDialog = inject<
  IStores,
  IScheduleAppointmentTaskDialogProps,
  Partial<IScheduleAppointmentTaskDialogProps>,
  any
>((stores: IStores) => ({
  close: stores.scheduleAppointmentTasks.closeDialog,
  isLoading: stores.scheduleAppointmentTasks.isLoading,
  isOpen: stores.scheduleAppointmentTasks.isModalOpen,
  saveScheduleAppointmentTask:
    stores.scheduleAppointmentTasks.saveScheduleAppointmentTask,
  scheduleAppointmentTask:
    stores.scheduleAppointmentTasks.selectedScheduleAppointmentTask,
  user: stores.global.user,
}))(ScheduleAppointmentTaskDialog)

export default InjectedScheduleAppointmentTaskDialog
